import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';


const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29.99,
    period: '/month',
    searches: 200,
    searchLabel: '200 comp searches/mo',
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    badge: null,
    features: [
      '500 ARV + comps searches per month',
      'Up to 6 comparable properties per analysis',
      'Price per sqft breakdown for each comp',
      'ARV calculated from avg price/sqft × subject sqft',
      'Sale date, address, beds/baths/sqft per comp',
      'Chrome extension sidebar integration',
      'Works on Zillow, Realtor.com, Redfin',
      'Session-based — no listing data stored',
      'Email support',
    ],
    notIncluded: [
      'Bulk property analysis',
      'Export to CSV/PDF',
      'Priority support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49.99,
    period: '/month',
    searches: 500,
    searchLabel: '500 comp searches/mo',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 100%)',
    badge: 'Most Popular',
    features: [
      '1,000 ARV + comps searches per month',
      'Up to 6 comparable properties per analysis',
      'Price per sqft breakdown for each comp',
      'ARV calculated from avg price/sqft × subject sqft',
      'Sale date, address, beds/baths/sqft per comp',
      'Chrome extension sidebar integration',
      'Works on Zillow, Realtor.com, Redfin',
      'Session-based — no listing data stored',
      'Priority email support',
      'Usage dashboard with search history',
    ],
    notIncluded: [
      'Bulk property analysis',
      'Export to CSV/PDF',
    ],
  },
  {
    id: 'unlimited',
    name: 'Unlimited',
    price: 69.99,
    period: '/month',
    searches: -1,
    searchLabel: 'Unlimited searches',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #78350f 0%, #b45309 100%)',
    badge: 'Best Value',
    features: [
      'Unlimited ARV + comps searches',
      'Up to 6 comparable properties per analysis',
      'Price per sqft breakdown for each comp',
      'ARV calculated from avg price/sqft × subject sqft',
      'Sale date, address, beds/baths/sqft per comp',
      'Chrome extension sidebar integration',
      'Works on Zillow, Realtor.com, Redfin',
      'Session-based — no listing data stored',
      'Priority email + chat support',
      'Usage dashboard with search history',
      'Early access to new features',
    ],
    notIncluded: [],
  },
];

const FAQS = [
  {
    q: 'What counts as a "comp search"?',
    a: 'Each time you click "Run ARV Analysis" in the extension sidebar on a property listing, that counts as one comp search. You can view the same result multiple times without it counting again — only new analysis requests are counted.',
  },
  {
    q: 'How does the ARV calculation work?',
    a: 'The extension finds recently sold comparable properties near the subject address. It calculates the price per square foot for each comp, then multiplies the average price/sqft by the subject property\'s square footage to arrive at the ARV. The formula is: ARV = Avg(sold price ÷ sqft) × subject sqft.',
  },
  {
    q: 'Does the extension store my property data?',
    a: 'No. All property data is session-based and never persisted to a database. Your search count is tracked server-side for billing purposes, but no listing details, addresses, or ARV results are stored long-term.',
  },
  {
    q: 'Which listing sites does the extension support?',
    a: 'The extension works on Zillow, Realtor.com, and Redfin property detail pages. It auto-opens the sidebar whenever you navigate to a property listing.',
  },
  {
    q: 'What happens when I hit my monthly search limit?',
    a: 'The ARV Analysis tab will show a usage limit message with options to upgrade. You can still view listing details (price, beds, baths, sqft) — only the ARV analysis tool is gated.',
  },
  {
    q: 'Can I upgrade or downgrade my plan?',
    a: 'Yes. Plan changes take effect at the start of your next billing cycle. Unused searches do not roll over between months.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — new accounts get a 3-day free trial with 10 comp searches. After the trial period, a valid payment method is required to continue running ARV analyses.',
  },
  {
    q: 'How do I activate my subscription after purchasing?',
    a: 'After registering and completing payment, your account is automatically upgraded to the selected tier. Log in to the extension using the same email and password, and the ARV analysis will be unlocked immediately.',
  },
];

function UsageMeter({ used, limit, color }) {
  if (limit === -1) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
          <span>{used.toLocaleString()} searches used</span>
          <span style={{ color }}>Unlimited</span>
        </div>
        <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: '100%', height: '100%', background: `linear-gradient(90deg, ${color}, ${color}88)`, borderRadius: 3 }} />
        </div>
      </div>
    );
  }
  const pct = Math.min((used / limit) * 100, 100);
  const barColor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : color;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
        <span>{used.toLocaleString()} / {limit.toLocaleString()} searches used</span>
        <span style={{ color: barColor }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${barColor}, ${barColor}88)`, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
        {limit - used > 0 ? `${(limit - used).toLocaleString()} searches remaining this month` : 'Limit reached — upgrade to continue'}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subStatus, setSubStatus] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [annual, setAnnual] = useState(false);
const [checkoutLoading, setCheckoutLoading] = useState(null); // tier being purchased
  const [checkoutMsg, setCheckoutMsg] = useState('');

  // ── Load subscription status ────────────────────────────────
  const loadSubStatus = () => {
    const token = localStorage.getItem('proparv_token');
    if (!token) return;
    setLoadingSub(true);
    fetch('/api/subscription/status', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        setSubStatus(data);
        if (user) updateUser({ ...user, subscription: data });
      })
      .catch(() => {})
      .finally(() => setLoadingSub(false));
  };

  useEffect(() => {
    if (!user) return;
    loadSubStatus();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle Stripe redirect back ─────────────────────────────
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    const tier      = searchParams.get('tier');

    if (cancelled) {
      setCheckoutMsg('Payment cancelled — no charge was made.');
      return;
    }

    if (sessionId && user) {
      const token = localStorage.getItem('proparv_token');
      if (!token) return;
      setCheckoutMsg('Verifying payment...');
      fetch(`/api/stripe/verify-session?session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setCheckoutMsg(`Payment successful! Your ${tier || ''} plan is now active.`);
            loadSubStatus();
          } else {
            setCheckoutMsg('Could not verify payment. Please contact support.');
          }
        })
        .catch(() => setCheckoutMsg('Could not verify payment. Please contact support.'));

      // Clean query params from URL without reload
      window.history.replaceState({}, '', '/pricing');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTier  = subStatus?.tier || (user ? 'free' : null);
  const isActivePlan = (planId) => currentTier === planId && subStatus?.status === 'active';

  const getCtaLabel = (planId) => {
    if (!user) return 'Start 3-Day Free Trial';
    if (isActivePlan(planId)) return 'Current Plan';
    if (checkoutLoading === planId) return 'Redirecting...';
    if (!subStatus?.status || subStatus.status !== 'active' || currentTier === 'free' || currentTier === 'trial')
      return 'Start 3-Day Free Trial';
    const order = ['starter', 'pro', 'unlimited'];
    return order.indexOf(planId) > order.indexOf(currentTier) ? 'Upgrade' : 'Downgrade';
  };

  // ── Stripe Checkout ─────────────────────────────────────────
  const handleCta = async (planId) => {
    if (!user) { navigate(`/register?tier=${planId}&annual=${annual}`); return; }
    if (isActivePlan(planId)) return;

    const token = localStorage.getItem('proparv_token');
    if (!token) { navigate('/login?next=/pricing'); return; }

    setCheckoutLoading(planId);
    setCheckoutMsg('');
    try {
      const res  = await fetch('/api/stripe/checkout', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ tier: planId, annual }),
      });
      const data = await res.json();
      if (res.status === 401) {
        // Token expired — log out and redirect to login
        localStorage.removeItem('proparv_token');
        localStorage.removeItem('proparv_user');
        navigate('/login?next=/pricing');
        return;
      }
      if (data.url) {
        window.location.href = data.url; // redirect to Stripe Checkout
      } else {
        setCheckoutMsg(data.message || data.error || 'Could not start checkout. Please try again.');
        setCheckoutLoading(null);
      }
    } catch {
      setCheckoutMsg('Cannot reach server. Make sure the backend is running.');
      setCheckoutLoading(null);
    }
  };

const annualDiscount = 0.2;

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh', background: 'var(--bg-dark)' }}>

      {/* Checkout status banner */}
      {checkoutMsg && (
        <div style={{
          position: 'sticky', top: 64, zIndex: 100,
          padding: '14px 24px',
          background: checkoutMsg.includes('successful') ? 'rgba(16,185,129,0.12)' : checkoutMsg.includes('cancelled') ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
          borderBottom: `1px solid ${checkoutMsg.includes('successful') ? 'rgba(16,185,129,0.3)' : checkoutMsg.includes('cancelled') ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: checkoutMsg.includes('successful') ? '#34d399' : checkoutMsg.includes('cancelled') ? '#fbbf24' : '#fca5a5',
          textAlign: 'center', fontWeight: 600, fontSize: '0.95rem'
        }}>
          {checkoutMsg}
        </div>
      )}

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '72px 20px 48px', background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(37,99,235,0.15) 0%, transparent 70%)' }}>
        <div style={{ display: 'inline-block', background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.3)', borderRadius: 20, padding: '6px 16px', fontSize: '0.8rem', color: '#60a5fa', fontWeight: 600, marginBottom: 20, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Simple, Transparent Pricing
        </div>
        <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.15, marginBottom: 16, letterSpacing: '-0.03em' }}>
          Invest smarter with <span style={{ color: 'var(--primary-light)' }}>real ARV data</span>
        </h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: 560, margin: '0 auto 32px', lineHeight: 1.7 }}>
          Every plan includes full property details on any listing — no paywall on browsing. Only ARV analysis is a paid feature.
        </p>



        {/* Logged-in status banner */}
        {user && (
          <div style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 20px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            <span>👤</span>
            <span>Signed in as <strong style={{ color: 'var(--text-primary)' }}>{user.email}</strong></span>
            {loadingSub && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Loading plan…</span>}
            {!loadingSub && subStatus && (
              <span style={{
                background: currentTier === 'free' ? 'rgba(255,255,255,0.08)' : currentTier === 'unlimited' ? 'rgba(245,158,11,0.15)' : currentTier === 'pro' ? 'rgba(139,92,246,0.15)' : 'rgba(59,130,246,0.15)',
                color: currentTier === 'free' ? 'var(--text-muted)' : currentTier === 'unlimited' ? '#fbbf24' : currentTier === 'pro' ? '#a78bfa' : '#60a5fa',
                border: `1px solid ${currentTier === 'free' ? 'rgba(255,255,255,0.1)' : currentTier === 'unlimited' ? 'rgba(245,158,11,0.3)' : currentTier === 'pro' ? 'rgba(139,92,246,0.3)' : 'rgba(59,130,246,0.3)'}`,
                borderRadius: 8, padding: '2px 10px', fontWeight: 700, fontSize: '0.8rem', textTransform: 'capitalize'
              }}>
                {currentTier === 'free' ? 'Free Plan' : `${currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Plan Cards */}
      <div className="container" style={{ paddingBottom: 64 }}>

        {/* Billing toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 4, gap: 4
          }}>
            <button
              onClick={() => setAnnual(false)}
              style={{
                padding: '10px 28px', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.9rem', transition: 'all 0.2s',
                background: !annual ? 'var(--primary)' : 'transparent',
                color: !annual ? '#fff' : 'var(--text-secondary)',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              style={{
                padding: '10px 28px', borderRadius: 9, border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.9rem', transition: 'all 0.2s',
                background: annual ? 'var(--primary)' : 'transparent',
                color: annual ? '#fff' : 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              Annual
              <span style={{
                background: annual ? 'rgba(255,255,255,0.2)' : 'rgba(34,197,94,0.15)',
                color: annual ? '#fff' : '#4ade80',
                border: annual ? 'none' : '1px solid rgba(34,197,94,0.3)',
                borderRadius: 8, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
              }}>
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, maxWidth: 1080, margin: '0 auto' }}>
          {PLANS.map(plan => {
            const active = isActivePlan(plan.id);
            const monthlyPrice = plan.price;
            const annualMonthlyPrice = parseFloat((plan.price * (1 - annualDiscount)).toFixed(2));
            const displayPrice = annual ? annualMonthlyPrice : monthlyPrice;
            const annualTotal = parseFloat((annualMonthlyPrice * 12).toFixed(2));
            const annualSavings = parseFloat(((monthlyPrice - annualMonthlyPrice) * 12).toFixed(2));

            return (
              <div
                key={plan.id}
                style={{
                  background: active ? plan.gradient : 'var(--bg-card)',
                  border: `2px solid ${active ? plan.color : plan.id === 'pro' ? 'rgba(139,92,246,0.4)' : 'var(--border)'}`,
                  borderRadius: 16,
                  padding: '32px 28px',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: active ? `0 8px 32px ${plan.color}33` : plan.id === 'pro' ? '0 4px 24px rgba(139,92,246,0.15)' : 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 40px ${plan.color}44`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = active ? `0 8px 32px ${plan.color}33` : plan.id === 'pro' ? '0 4px 24px rgba(139,92,246,0.15)' : 'none'; }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: plan.color, color: '#fff', fontWeight: 700, fontSize: '0.75rem',
                    padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: '0.04em',
                    textTransform: 'uppercase', boxShadow: `0 4px 12px ${plan.color}66`
                  }}>
                    {plan.badge}
                  </div>
                )}
                {active && (
                  <div style={{
                    position: 'absolute', top: -14, right: 24,
                    background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: '0.72rem',
                    padding: '4px 12px', borderRadius: 20, letterSpacing: '0.04em', textTransform: 'uppercase'
                  }}>
                    ✓ Active
                  </div>
                )}

                {/* Plan name + color dot */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: plan.color, boxShadow: `0 0 8px ${plan.color}` }} />
                  <span style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>{plan.name}</span>
                </div>

                {/* Price */}
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: '2.6rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.04em' }}>${displayPrice}</span>
                  <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginLeft: 4 }}>/month</span>
                </div>

                {/* Annual billing line */}
                {annual ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                      Billed ${annualTotal}/year
                    </span>
                    <span style={{
                      background: 'rgba(34,197,94,0.15)', color: '#4ade80',
                      border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8,
                      padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
                    }}>
                      Save ${annualSavings}/yr
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                    Billed monthly &nbsp;·&nbsp;
                    <span style={{ color: '#4ade80' }}>Save ${annualSavings}/yr with annual</span>
                  </div>
                )}

                {/* Search count highlight */}
                <div style={{ background: `${plan.color}18`, border: `1px solid ${plan.color}33`, borderRadius: 8, padding: '8px 14px', marginBottom: 20, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: plan.color, fontWeight: 800, fontSize: '0.95rem' }}>
                    {plan.searches === -1 ? '∞ Unlimited' : plan.searches.toLocaleString()}
                  </span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>ARV analyses/month</span>
                </div>

                {/* Usage meter (if this is user's current plan) */}
                {active && subStatus && (
                  <UsageMeter
                    used={subStatus.searchesUsed || 0}
                    limit={plan.searches}
                    color={plan.color}
                  />
                )}

                {/* CTA */}
                <button
                  onClick={() => handleCta(plan.id)}
                  disabled={active}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 10, fontWeight: 700, fontSize: '0.95rem',
                    cursor: active ? 'default' : 'pointer', border: 'none', marginTop: 20, marginBottom: 24,
                    background: active ? 'rgba(34,197,94,0.15)' : plan.color,
                    color: active ? '#4ade80' : '#fff',
                    boxShadow: active ? 'none' : `0 4px 14px ${plan.color}55`,
                    transition: 'opacity 0.2s, transform 0.15s',
                    opacity: active ? 0.9 : 1,
                  }}
                  onMouseEnter={e => { if (!active) { e.target.style.opacity = '0.85'; e.target.style.transform = 'scale(1.01)'; } }}
                  onMouseLeave={e => { e.target.style.opacity = '1'; e.target.style.transform = 'scale(1)'; }}
                >
                  {getCtaLabel(plan.id)}
                </button>

                {/* Divider */}
                <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 20 }} />

                {/* Features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span style={{ color: plan.color, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                      <span>{f}</span>
                    </div>
                  ))}
                  {plan.notIncluded.map((f, i) => (
                    <div key={`no-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.875rem', color: 'var(--text-muted)', opacity: 0.5 }}>
                      <span style={{ flexShrink: 0, marginTop: 1 }}>✕</span>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Free tier note */}
        <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          All plans include free access to property details (price, beds, baths, sqft, facts) — no subscription needed to browse listings.
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '72px 20px' }}>
        <div className="container" style={{ maxWidth: 960, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Compare Plans</h2>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: 40 }}>Everything you get with each tier</p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '2px solid var(--border)', width: '40%' }}>Feature</th>
                  {PLANS.map(p => (
                    <th key={p.id} style={{ textAlign: 'center', padding: '12px 16px', color: isActivePlan(p.id) ? p.color : 'var(--text-secondary)', fontWeight: 700, borderBottom: `2px solid ${isActivePlan(p.id) ? p.color : 'var(--border)'}` }}>
                      {p.name}
                      {isActivePlan(p.id) && <div style={{ fontSize: '0.7rem', color: '#4ade80' }}>▲ Your plan</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'ARV analyses / month', vals: ['200', '500', 'Unlimited'] },
                  { label: 'Comps per analysis', vals: ['Up to 6', 'Up to 6', 'Up to 6'] },
                  { label: 'Price per sqft per comp', vals: ['✓', '✓', '✓'] },
                  { label: 'Sale date & address', vals: ['✓', '✓', '✓'] },
                  { label: 'Beds / baths / sqft per comp', vals: ['✓', '✓', '✓'] },
                  { label: 'Property details (free)', vals: ['✓', '✓', '✓'] },
                  { label: 'Chrome extension integration', vals: ['✓', '✓', '✓'] },
                  { label: 'Zillow / Redfin / Realtor.com', vals: ['✓', '✓', '✓'] },
                  { label: 'Usage dashboard', vals: ['—', '✓', '✓'] },
                  { label: 'Priority support', vals: ['—', '✓', '✓'] },
                  { label: 'Early feature access', vals: ['—', '—', '✓'] },
                ].map((row, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '13px 16px', color: 'var(--text-secondary)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>{row.label}</td>
                    {row.vals.map((v, vi) => (
                      <td key={vi} style={{ textAlign: 'center', padding: '13px 16px', color: v === '✓' ? PLANS[vi].color : v === '—' ? 'var(--text-muted)' : 'var(--text-primary)', fontWeight: v === '✓' || v === '—' ? 600 : 700, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div style={{ padding: '72px 20px' }}>
        <div className="container" style={{ maxWidth: 860, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>How ARV Analysis Works</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 48, lineHeight: 1.7 }}>The extension does the math so you can evaluate deals in seconds, right from the listing page.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 28 }}>
            {[
              { step: '01', title: 'Browse Listings', desc: 'Visit any property on Zillow, Redfin, or Realtor.com. The PROPARV sidebar opens automatically.' },
              { step: '02', title: 'Run ARV Analysis', desc: 'Click "Run ARV Analysis" in the sidebar. The extension sends the address and sqft to our API.' },
              { step: '03', title: 'Comps Are Found', desc: 'We find recently sold comparable properties nearby and calculate price per sqft for each one.' },
              { step: '04', title: 'ARV Calculated', desc: 'ARV = Average price/sqft of comps × subject property sqft. Instant, data-backed valuation.' },
            ].map(item => (
              <div key={item.step} className="card" style={{ padding: '28px 22px', textAlign: 'left' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--primary-light)', letterSpacing: '0.1em', marginBottom: 8 }}>{item.step}</div>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)', marginBottom: 12 }} />
                <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid var(--border)', padding: '72px 20px' }}>
        <div className="container" style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>Frequently Asked Questions</h2>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: 48 }}>Everything you need to know about PROPARV plans</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FAQS.map((faq, i) => (
              <div
                key={i}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem' }}>
                  <span>{faq.q}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem', transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(180deg)' : 'none', flexShrink: 0, marginLeft: 16 }}>▾</span>
                </div>
                {openFaq === i && (
                  <div style={{ padding: '0 22px 18px', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7, borderTop: '1px solid var(--border)' }}>
                    <div style={{ paddingTop: 14 }}>{faq.a}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div style={{ padding: '80px 20px', textAlign: 'center', background: 'radial-gradient(ellipse 70% 80% at 50% 50%, rgba(37,99,235,0.12) 0%, transparent 70%)' }}>
        <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 900, color: 'var(--text-primary)', marginBottom: 16, letterSpacing: '-0.03em' }}>
          Start analyzing deals today
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 32, fontSize: '1rem', maxWidth: 440, margin: '0 auto 32px' }}>
          Browse listings for free. Unlock ARV + comps analysis when you're ready to invest.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary btn-lg">Go to Dashboard →</Link>
          ) : (
            <>
              <Link to="/register" className="btn btn-primary btn-lg">Create Free Account</Link>
              <Link to="/login" className="btn btn-outline btn-lg">Sign In</Link>
            </>
          )}
        </div>
      </div>

    </div>
  );
}
